import { Enums } from '../common/enums'

import { DataProvider } from '../../helpers/data-provider'
import { TypeHelper } from '../../helpers/type-helper'

import { OpActivity } from './op-activity'
import { OpActor } from './op-actor'
import { OpDistributor } from './op-distributor'
import { OpLocation } from './op-location'
import { OpOperatorData } from './op-operator-data'
import { OpOperatorTechnicalPermission } from './op-operator-technical-permission'
import { OpProfile } from './op-profile'
import { OpRole } from './op-role'

import { OpMapArea, OpPolygon, OpRange } from '../custom/op-map-area'

import { EmailNotificationOptions } from '../util/email-notification-options'
import { IOpObject, IReferenceType, IOpChangeState, IOpData, ICopyable } from '../util/interfaces'
import { OpDataList } from '../util/op-data-list'
import { OpDataUtil } from '../util/op-data-util'
import { SmsNotificationOptions } from '../util/sms-notification-options'
import { Utils } from '../util/utils'

export class OpOperator implements IOpObject, IReferenceType, IOpChangeState, ICopyable<OpOperator> {

    //#region Properties

    public description: string
    public idActor: number
    public idDistributor: number
    public idOperator: number
    public isBlocked: boolean
    public login: string
    public serialNbr: number

    //#endregion


    //#region NavigationProperties

    private _actor: OpActor
    public get actor(): OpActor {
        return this._actor
    }
    public set actor(value: OpActor) {
        this._actor = value
        this.idActor = value != null ? value.idActor : null
    }

    private _distributor: OpDistributor
    public get distributor(): OpDistributor {
        return this._distributor
    }
    public set distributor(value: OpDistributor) {
        this._distributor = value
        this.idDistributor = value != null ? value.idDistributor : null
    }

    //#endregion


    //#region CustomProperties

    public activities: OpActivity[] = []
    public allActivities: OpActivity[] = []
    public area: OpMapArea = null

    private _depositoryLocation: OpLocation
    public set depositoryLocation(value: OpLocation) {
        this._depositoryLocation = value
        this.idDepositoryLocation = value != null ? value.idLocation : null
    }
    public get depositoryLocation(): OpLocation {
        return this._depositoryLocation
    }

    public set idDepositoryLocation(value: number) {
        this.dataList.setValue(value, Enums.DataType.DEPOSITORY_ID)
    }

    public get idDepositoryLocation(): number {
        return this.dataList.getValue(Enums.DataType.DEPOSITORY_ID)
    }

    public profile: OpProfile
    public roles: OpRole[]
    public emailNotificationOptions: EmailNotificationOptions
    public smsNotificationOptions: SmsNotificationOptions
    public operatorTechnicalPermissions: OpOperatorTechnicalPermission[]

    public dynamicValues: { Key: string, Value: any }[]

    public get avatar(): string {
        if (this.actor != null && this.actor.avatar != null) {
            return this.actor.avatar
        }

        return null
    }

    //#endregion


    //#region Ctor

    constructor(opObj: any) {
        if (opObj instanceof OpOperator) {
            this.copy(<OpOperator>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    //#endregion


    //#region IOpChangeState Implementation

    public opState: Enums.OpChangeState

    //#endregion


    //#region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdOperator
    }

    public getReferenceKey(): number {
        return this.idOperator
    }

    public getReferenceValue(): any {
        return this

    }

    //#endregion


    //#region IOpObject Implementation

    public get idObject(): any {
        return this.idOperator
    }

    public set idObject(idObject: any) {
        this.idOperator = idObject
    }

    public dataList: OpDataList<OpOperatorData> = new OpDataList<OpOperatorData>()

    public customFormatString: string

    public fromJSON(opObj: any, compressed: boolean = false): void {

        if (opObj) {
            if (opObj.DataList) {
                this.dataList.push(...opObj.DataList.map(data => new OpOperatorData(data)))
            } else {
                this.dataList = new OpDataList<OpOperatorData>()
            }

            this.activities = opObj.Activities != null ? opObj.Activities.map((activityObj: any) => new OpActivity(activityObj)) : null
            this.actor = opObj._Actor != null ? new OpActor(opObj._Actor) : null
            this.allActivities = opObj.AllActivities != null ? opObj.AllActivities.map((activityObj: any) => new OpActivity(activityObj)) : null
            this.depositoryLocation = opObj._DepositoryLocation ? new OpLocation(opObj._DepositoryLocation) : null
            this.distributor = opObj._Distributor != null ? new OpDistributor(opObj._Distributor) : null
            this.emailNotificationOptions = opObj.EmailNotificationOptions != null ? new EmailNotificationOptions(opObj.EmailNotificationOptions) : null
            this.smsNotificationOptions = opObj.SmsNotificationOptions != null ? new SmsNotificationOptions(opObj.SmsNotificationOptions) : null
            this.roles = opObj.Roles != null ? opObj.Roles.map((roleObj: any) => new OpRole(roleObj)) : null
            this.operatorTechnicalPermissions = opObj.OperatorTechnicalPermissions != null ? opObj.OperatorTechnicalPermissions.map((tpObj: any) => new OpOperatorTechnicalPermission(tpObj)) : null

            if (opObj.Area != null) {
                if (opObj.Area.__type != null && opObj.Area.__type.indexOf('OpMapControl.OpPolygon') > -1) {
                    this.area = new OpPolygon(opObj.Area)
                } else if (opObj.Area.__type != null && opObj.Area.__type.indexOf('OpMapControl.OpRange') > -1) {
                    this.area = new OpRange(opObj.Area)
                } else {
                    this.area = new OpMapArea(opObj.Area)
                }
            }

            if (opObj.Profile != null) {
                this.profile = new OpProfile(opObj.Profile)
            }

            this.idOperator = opObj.ID_OPERATOR
            this.idActor = opObj.ID_ACTOR
            this.idDistributor = opObj.ID_DISTRIBUTOR
            this.serialNbr = opObj.SERIAL_NBR
            this.login = opObj.LOGIN
            this.isBlocked = opObj.IS_BLOCKED
            this.description = opObj.DESCRIPTION
            this.dynamicValues = opObj.dynamicValues

            this.opState = opObj.OpState
        } else {
            this.idOperator = 0
            this.opState = Enums.OpChangeState.New
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_OPERATOR: this.idOperator,
            ID_ACTOR: this.idActor,
            ID_DISTRIBUTOR: this.idDistributor,
            SERIAL_NBR: this.serialNbr,
            LOGIN: this.login,
            IS_BLOCKED: this.isBlocked,
            DESCRIPTION: this.description,

            _Actor: this.actor ? this.actor.toJSON(compressed) : null,

            Activities: this.activities ? this.activities.map((activity: OpActivity) => activity.toJSON(compressed)) : null,
            AllActivities: this.allActivities ? this.allActivities.map((activity: OpActivity) => activity.toJSON(compressed)) : null,
            DataList: this.dataList ? this.dataList.ToJSON(compressed) : null,
            EmailNotificationOptions: this.emailNotificationOptions != null ? this.emailNotificationOptions.toJSON(compressed) : null,
            OperatorTechnicalPermissions: this.operatorTechnicalPermissions != null ? this.operatorTechnicalPermissions.map((technicalPermission: OpOperatorTechnicalPermission) => technicalPermission.toJSON(compressed)) : null,
            Profile: this.profile ? this.profile.toJSON(compressed) : null,
            Roles: this.roles ? this.roles.map((role: OpRole) => role.toJSON()) : null,
            SmsNotificationOptions: this.smsNotificationOptions != null ? this.smsNotificationOptions.toJSON(compressed) : null,

            OpState: this.opState
        }
    }

    public toString() {
        if (this.actor != null) {
            if (this.actor.surname != null) {
                return this.actor.surname + ' ' + this.actor.name
            }

            if (this.actor.name != null) {
                return this.actor.name
            }
        }

        return this.login
    }

    //#endregion


    //#region Permission

    public hasPermission(idActivity: number, referenceValue = null, defaultIfNoPermissionDefined = false) {
        // Metoda została przeniesiona z RoleComponent, ponieważ z poziomu UI.Business.Object nie mielibyśmy do niej dostępu.

        if (this.activities != null) {
            var activites = this.activities.filter(w => w.idActivity == idActivity)
            //szukamy czy operator ma zdefiniowane idActivity i nie jest ustawione Deny na True
            if (referenceValue == null) {
                if (activites.length == 0) {
                    // nie ma takich uprawnien
                    return defaultIfNoPermissionDefined
                }
                return activites.some(a => !a.deny) && !activites.some(a => a.deny)
            }
            //podano ID obiektu do ktorego mamy sprawdzić uprawnienie
            else {
                if (activites.length == 0) {
                    // nie ma takich uprawnien
                    return defaultIfNoPermissionDefined
                } else if (activites.some(w => OpDataUtil.valueEquals(w.referenceValue, referenceValue) && w.deny)) {
                    //zdefiniowane uprawnienie z tym referenceValue z Deny = True
                    return false
                } else if (activites.some(w => w.referenceValue == null && w.deny)) {
                    //wszystkie elementy sa wykluczone
                    return false
                } else if (activites.some(w => w.referenceValue == null && !w.deny)) {
                    //wszystkie elementy dozwolone
                    return true
                } else if (activites.some(w => OpDataUtil.valueEquals(w.referenceValue, referenceValue) && !w.deny)) {
                    //zdefiniowane uprawnienie z tym referenceValue z Deny = False
                    return true
                } else if (defaultIfNoPermissionDefined && !activites.some(w => OpDataUtil.valueEquals(w.referenceValue, referenceValue) && !w.deny)) {
                    // 09.11.2017 - Po uzgodnieniu z Sławek Dudzik, Łukasz Pieła
                    // Sytuacja w której operator miał zdefiniowane uprawnienia tylko na DENY, ale nie na ALL, przykładowo
                    //     LocationType = 4, DENY = true
                    //     LocationType = 8, DENY = true
                    // podczas sprawdzania czy mamy uprawnienia do np. LocationType = 1 (które nie było nijak zdefiniowane w uprawnieniach) zwracało
                    // nam false (brak uprawnień) co mijało się z założeniem, że brak zdefiniowanego uprawnienia = posiadanie uprawnienia.
                    // W celu uniknięcia dodatokwych błędów, sprawdzamy czy jesteśmy w sytuacji w której mamy zdefiniowane TYLKO
                    // uprawnienia z DENY = true i jeśli tak zwracamy domyślną wartość a w pozostałych przypadkach false.
                    // Pozostałe przypadki konfiguracji uprawnień powinny być pochytane w powyższych ifach.
                    if (activites.filter(w => !w.deny).length == 0) {
                        return defaultIfNoPermissionDefined
                    }

                    // nie ma zdefiniowanego uprawnienia z tym referenceValue z Deny = False
                    return false
                }
            }
        }
        return defaultIfNoPermissionDefined //jesli nie ma zdefiniowanych uprawnień
    }

    //#endregion


    //#region ICopyable Implementation

    public clone(includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpOperator {
        const newOperator = new OpOperator(null)

        newOperator.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return newOperator
    }

    public copy(source: OpOperator, includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): void {
        if (source == null) {
            return
        }

        if (includeNavigationProperties) {
            if (source.actor != null) {
                this.actor = source.actor.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }

            if (source.distributor) {
                this.distributor = source.distributor.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }
        }

        if (includeCustomProperties) {
            if (source.activities != null) {
                this.activities = [...source.activities.map((activity: OpActivity) => activity.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))]
            }

            if (source.allActivities != null) {
                this.allActivities = [...source.allActivities.map((activity: OpActivity) => activity.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))]
            }

            if (source.roles != null) {
                this.roles = [...source.roles.map((role: OpRole) => role.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))]
            }

            if (source.operatorTechnicalPermissions != null) {
                this.operatorTechnicalPermissions = [...source.operatorTechnicalPermissions.map((technicalPermission: OpOperatorTechnicalPermission) => technicalPermission.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))]
            }

            this.area = source.area                                             // TODO: Copy methods for those types
            this.profile = source.profile                                       // TODO: Copy methods for those types

            if (source.emailNotificationOptions != null) {
                this.emailNotificationOptions = source.emailNotificationOptions.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }

            if (source.smsNotificationOptions != null) {
                this.smsNotificationOptions = source.smsNotificationOptions.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }
        }

        if (includeDataList && source.dataList != null) {
            this.dataList = source.dataList.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
        }

        if (includeDynamicValues) {
            this.dynamicValues = TypeHelper.clone(source.dynamicValues, true)
        }

        this.idOperator = source.idOperator
        this.idActor = source.idActor
        this.idDistributor = source.idDistributor
        this.serialNbr = source.serialNbr
        this.login = source.login
        this.isBlocked = source.isBlocked
        this.description = source.description

        this.opState = source.opState
    }

    //#endregion


    //#region loadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpOperator[]): JQueryPromise<OpOperator[]> {
        return Utils.loadDataListNavigationProperties(dataProvider, list)
    }

    //#endregion


    //#region fromIOpObject

    public static fromIOpObject(object: IOpObject): OpOperator {
        const newObject: OpOperator = new OpOperator(null)

        newObject.idOperator = object.idObject
        newObject.opState = object.opState
        newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpOperatorData.fromIOpData(data)))

        return newObject
    }

    //#endregion
}